<script setup lang="ts">
// 2026-08-05 risk-screen-per-fund, Task 7: shared tile for the RiskPanel
// hero row (BGA | JH | Gold | Combined). Renders either:
//   - the standard sharpe/dd/cagr/win-rate readout + colour-coded
//     classification badge, or
//   - a warm-up badge (n / 20 nonzero days) when the stream hasn't yet
//     cleared RiskMetricsCalculator's warm-up threshold. During warm-up,
//     the underlying metric fields are already null server-side (see
//     DailyRiskMetricRow.IsWarmingUp doc comment) so they render as '—'
//     — this component additionally forces the classification readout to
//     neutral grey instead of the (meaningless, defaults-to-green) raw
//     classification value while warming up.
import type { DailyRiskMetricRow } from '../api/client'
import { computed } from 'vue'
import { streamCycleWinRate, type NavRisk } from './riskPanelHelpers'

const props = defineProps<{
  title: string
  row: DailyRiskMetricRow | null
  /** 2026-10-11 gated-fund preview: with no row, say so explicitly instead of a bare '—'. */
  emptyNote?: string
  /** Fund-level NAV risk (buy & hold official NAV) shown under emptyNote when available. */
  navRisk?: NavRisk | null
}>()

const isWarmingUp = computed(() => props.row?.isWarmingUp === true)

// Trade-cycle win rate for THIS tile's stream. Uses the same helper as
// the hero Win Rate card so the fund tiles and the hero always agree
// (previously the tile showed daily win rate — a totally different
// metric — causing confusion between eg 15% daily vs 42.9% cycle).
// Every fund stream carries its own fund's FIFO cycle fields (2026-09-09);
// rows with no cycle data yet render as '—'.
const cycleWinRate = computed(() => streamCycleWinRate(props.row))

function fmt(v: number | null | undefined, decimals = 2): string {
  return v !== null && v !== undefined ? v.toFixed(decimals) : '—'
}

function fmtPct(v: number | null | undefined, decimals = 1): string {
  return v !== null && v !== undefined ? (v * 100).toFixed(decimals) + '%' : '—'
}

function fmtCycleWinRate(): string {
  const { rate, count } = cycleWinRate.value
  if (rate === null) return '—'
  return `${fmtPct(rate)} (${count})`
}

function classColour(cls: string): string {
  if (cls === 'green') return '#4caf50'
  if (cls === 'amber') return '#fbc02d'
  if (cls === 'red') return '#f44336'
  return '#888'
}
</script>

<template>
  <div class="risk-tile">
    <div class="tile-title">{{ title }}</div>

    <template v-if="!row && emptyNote">
      <div class="tile-empty-note" data-testid="gated-risk-empty-note">{{ emptyNote }}</div>
      <div v-if="navRisk" class="tile-metrics" data-testid="gated-risk-nav-risk">
        <div class="tile-caption">Fund NAV, buy &amp; hold — last {{ navRisk.days }}d to {{ navRisk.asOf }}</div>
        <div class="tile-row"><span class="tile-label">NAV vol (ann.)</span><span class="tile-value">{{ fmtPct(navRisk.vol) }}</span></div>
        <div class="tile-row"><span class="tile-label">NAV max DD</span><span class="tile-value">{{ fmtPct(navRisk.maxDd) }}</span></div>
        <div class="tile-row"><span class="tile-label">NAV current DD</span><span class="tile-value">{{ fmtPct(navRisk.currentDd) }}</span></div>
      </div>
      <div class="tile-classification neutral">NO POSITION</div>
    </template>

    <template v-else-if="!row">
      <div class="tile-empty">—</div>
    </template>

    <template v-else>
      <div v-if="isWarmingUp" class="warmup-badge">
        Warming up — {{ row.warmupNonzeroDays ?? 0 }} / 20 nonzero days
      </div>

      <div class="tile-metrics" :class="{ 'is-warming': isWarmingUp }">
        <div class="tile-row"><span class="tile-label">Sharpe 120d</span><span class="tile-value">{{ fmt(row.sharpe120d) }}</span></div>
        <div class="tile-row"><span class="tile-label">Max DD 120d</span><span class="tile-value">{{ fmtPct(row.maxDd120d) }}</span></div>
        <div class="tile-row"
             :title="'Peak-to-current drawdown within this stream — no 60d/120d variant. Differs per stream because each stream has its own peak.'">
          <span class="tile-label">Current DD (stream)</span>
          <span class="tile-value">{{ fmtPct(row.currentDdPct) }}</span>
        </div>
        <div class="tile-row"><span class="tile-label">CAGR 120d</span><span class="tile-value">{{ fmtPct(row.cagr120d) }}</span></div>
        <div class="tile-row"
             :title="'Trade-cycle win rate — % of closed BUY→SELL round trips that were profitable, FIFO-paired within this fund. Matches the hero Win Rate card. (n) = cycle count.'">
          <span class="tile-label">Cycle win rate 120d</span>
          <span class="tile-value">{{ fmtCycleWinRate() }}</span>
        </div>
      </div>

      <div
        class="tile-classification"
        :class="{ neutral: isWarmingUp }"
        :style="isWarmingUp ? {} : { color: classColour(row.classification) }"
      >
        {{ isWarmingUp ? '—' : row.classification.toUpperCase() }}
      </div>
    </template>
  </div>
</template>

<style scoped>
.risk-tile {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 0.75rem;
  flex: 1;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.tile-title {
  font-size: 0.8rem;
  color: #aaa;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tile-empty {
  color: #888;
}

.warmup-badge {
  color: #888;
  font-style: italic;
  font-size: 0.8rem;
}

.tile-metrics {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
}

.tile-metrics.is-warming {
  color: #666;
}

.tile-row {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.tile-label { color: #888; }
.tile-value { color: #ccc; }
.tile-metrics.is-warming .tile-value { color: #666; }

.tile-classification {
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.05em;
}

.tile-classification.neutral { color: #888; }

.tile-empty-note { color: #aaa; font-size: 0.8rem; }
.tile-caption { color: #777; font-size: 0.7rem; margin-bottom: 0.1rem; }
</style>
